import React, { useState } from 'react';
import { supabase } from '../supabase';
import { APP_VERSION_LABEL, APP_TAGLINE } from './Header';
import wordmark from '../assets/jvista-wordmark.png';

const inputStyle = {
  width: '100%', padding: '10px 12px',
  background: '#131920', border: '1px solid #2a3f55', color: '#e5ecf3',
  fontFamily: 'IBM Plex Mono, monospace', fontSize: 14,
  borderRadius: 3, outline: 'none', boxSizing: 'border-box',
};
const labelStyle = {
  display: 'block', marginBottom: 4, fontSize: 11,
  color: '#8ba3bb', fontFamily: 'IBM Plex Mono, monospace',
  textTransform: 'uppercase', letterSpacing: 0.5,
};
const btnStyle = {
  width: '100%', padding: '12px',
  background: 'rgba(0,212,255,0.1)', border: '1px solid #00d4ff',
  color: '#00d4ff', borderRadius: 3, cursor: 'pointer',
  fontFamily: 'IBM Plex Mono, monospace', fontSize: 14,
};
const btnDisabled = { ...btnStyle, opacity: 0.4, cursor: 'not-allowed' };
const linkBtnStyle = {
  background: 'none', border: 'none', padding: 0,
  color: '#00d4ff', cursor: 'pointer',
  fontFamily: 'IBM Plex Mono, monospace', fontSize: 12,
};
const subtleLinkStyle = {
  ...linkBtnStyle, color: '#8ba3bb', fontSize: 11,
};

const _EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

// mode: 'login' (default) | 'setup' (first-time) | 'reset' (existing user)
const MODE_COPY = {
  setup: {
    prompt: "Enter your email above and we'll send you a password setup link.",
    button: 'Send Setup Link',
    busy:   'Sending setup link…',
    success: (email) =>
      `Check your inbox — we've sent you a password setup link for ${email}.`,
  },
  reset: {
    prompt: "Enter your email above and we'll send you a password reset link.",
    button: 'Send Reset Link',
    busy:   'Sending reset link…',
    success: (email) =>
      `Check your inbox — we've sent you a password reset link for ${email}.`,
  },
};

export default function LoginPanel() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [mode, setMode] = useState('login');   // 'login' | 'setup' | 'reset'
  // status: { kind: 'err' | 'ok' | 'info', text }
  const [status, setStatus] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!supabase) {
      setStatus({ kind: 'err', text:
        'Auth not configured — set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY.' });
      return;
    }
    setLoading(true);
    setStatus(null);
    const { error } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });
    setLoading(false);
    if (error) {
      setStatus({ kind: 'err', text: error.message || 'Sign in failed' });
    }
    // Success → App.js auth listener re-renders and mounts the main app
  }

  async function handleSendLink() {
    if (!supabase) {
      setStatus({ kind: 'err', text: 'Auth not configured.' });
      return;
    }
    const addr = email.trim();
    if (!_EMAIL_RE.test(addr)) {
      setStatus({ kind: 'err',
        text: 'Enter your email in the field above first.' });
      return;
    }
    setResetting(true);
    setStatus(null);
    const { error } = await supabase.auth.resetPasswordForEmail(addr, {
      redirectTo: window.location.origin,
    });
    setResetting(false);
    const copy = MODE_COPY[mode];
    if (error) {
      setStatus({ kind: 'err', text: error.message || 'Delivery failed' });
    } else {
      setStatus({ kind: 'ok', text: copy.success(addr) });
      // After a successful send, drop back to the login view so the
      // user sees the success banner above the Sign In button.
      setMode('login');
    }
  }

  function startMode(nextMode) {
    setMode(nextMode);
    setStatus(null);
  }

  function cancelMode() {
    setMode('login');
    setStatus(null);
  }

  const statusColors = {
    err:  { border: '#f87171', color: '#f87171', bg: 'rgba(248,113,113,0.05)' },
    ok:   { border: '#4ade80', color: '#4ade80', bg: 'rgba(74,222,128,0.05)' },
    info: { border: '#8ba3bb', color: '#8ba3bb', bg: 'rgba(139,163,187,0.05)' },
  };

  const busy = loading || resetting;
  const copy = MODE_COPY[mode];

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', background: '#080b0f',
      fontFamily: 'IBM Plex Mono, monospace',
    }}>
      <form onSubmit={handleSubmit} style={{
        width: 360, padding: 32,
        background: '#0b1016', border: '1px solid #1e2d3d',
        borderRadius: 6,
      }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <img src={wordmark} alt={`JVISTA — ${APP_TAGLINE}`}
               style={{ width: 220, maxWidth: '100%', display: 'block',
                        margin: '0 auto', borderRadius: 6 }} />
          <div style={{ fontSize: 10, color: '#8ba3bb',
                         letterSpacing: 1, marginTop: 8 }}>
            {APP_VERSION_LABEL} · {APP_TAGLINE}
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle} htmlFor="lp-email">Email</label>
          <input
            id="lp-email" type="email" required autoFocus
            style={inputStyle}
            value={email}
            onChange={e => setEmail(e.target.value)}
          />
        </div>

        {mode === 'login' && (
          <div style={{ marginBottom: 18 }}>
            <label style={labelStyle} htmlFor="lp-password">Password</label>
            <input
              id="lp-password" type="password" required
              style={inputStyle}
              value={password}
              onChange={e => setPassword(e.target.value)}
            />
          </div>
        )}

        {status && (
          <div style={{
            padding: 10, marginBottom: 14,
            border: `1px solid ${statusColors[status.kind].border}`,
            color: statusColors[status.kind].color,
            background: statusColors[status.kind].bg,
            fontSize: 12, borderRadius: 3, lineHeight: 1.5,
          }}>
            {status.text}
          </div>
        )}

        {mode === 'login' ? (
          <>
            <button type="submit"
                    style={busy ? btnDisabled : btnStyle}
                    disabled={busy}>
              {loading ? 'Signing in…' : 'Sign In'}
            </button>

            <div style={{
              marginTop: 20, paddingTop: 16,
              borderTop: '1px solid #1e2d3d',
              display: 'flex', flexDirection: 'column', gap: 8,
              alignItems: 'center',
            }}>
              <div style={{ fontSize: 12, color: '#8ba3bb' }}>
                New to JVISTA?{'  '}
                <button type="button" style={linkBtnStyle}
                        onClick={() => startMode('setup')}>
                  Set up your password →
                </button>
              </div>
              <div style={{ fontSize: 11, color: '#4a6278' }}>
                Forgot your password?{'  '}
                <button type="button" style={subtleLinkStyle}
                        onClick={() => startMode('reset')}>
                  Reset it
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            <div style={{
              marginBottom: 14, padding: 12,
              border: '1px solid #1e2d3d', borderRadius: 3,
              background: '#131920',
              fontSize: 12, color: '#b3c5d6', lineHeight: 1.5,
            }}>
              {copy.prompt}
            </div>
            <button type="button"
                    style={busy ? btnDisabled : btnStyle}
                    disabled={busy}
                    onClick={handleSendLink}>
              {resetting ? copy.busy : copy.button}
            </button>
            <div style={{ marginTop: 12, textAlign: 'center' }}>
              <button type="button" style={subtleLinkStyle}
                      onClick={cancelMode}
                      disabled={busy}>
                ← Back to sign in
              </button>
            </div>
          </>
        )}

        <div style={{ marginTop: 20, fontSize: 11, color: '#4a6278',
                       textAlign: 'center', lineHeight: 1.6 }}>
          Access is invite-only during the demo phase.
          <br />
          Contact amit@jphb.us for credentials.
        </div>
      </form>
    </div>
  );
}
